import React, { FC } from 'react';
import { StatData } from './types';

interface DashboardHeaderProps {
  fundraiserName: string;
  fundraiserCode: string;
  scope: string;
  stats?: StatData | null;
  role?: string;
  lastUpdated?: Date | null;
  onRefresh?: () => void;
}

const BRAND = '#163172';

const STAT_TILE_CSS = `
  .dash-stat-tiles { display: flex; gap: 10px; flex-wrap: wrap; }
  .dash-stat-tile {
    background: rgba(255,255,255,0.1);
    border-radius: 12px;
    padding: 14px 20px;
    min-width: 120px;
    flex: 1 1 120px;
  }
  @media (max-width: 480px) {
    .dash-stat-tile { flex: 1 1 calc(50% - 5px); max-width: calc(50% - 5px); min-width: 0; }
  }
`;

const fmt = (n: number) =>
  `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const pct = (v: number, g: number) => Math.min(Math.round((v / g) * 100), 100);

interface StatTileProps {
  label: string;
  value: string | number;
  sub?: string;
}

const StatTile: FC<StatTileProps> = ({ label, value, sub }) => (
  <div className="dash-stat-tile">
    <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(196,211,255,0.85)', marginBottom: 4 }}>
      {label}
    </p>
    <p style={{ fontSize: 22, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{value}</p>
    {sub && <p style={{ fontSize: 11, color: 'rgba(196,211,255,0.7)', marginTop: 2 }}>{sub}</p>}
  </div>
);

const DashboardHeader: FC<DashboardHeaderProps> = ({
  fundraiserName,
  fundraiserCode,
  scope,
  stats,
  role = 'Coordinator',
  lastUpdated,
  onRefresh,
}) => {
  const progress = stats?.goal && stats.total_raised != null ? pct(stats.total_raised, stats.goal) : null;
  const daysValue = stats?.days_remaining != null ? stats.days_remaining : '—';
  const daysSub   = stats?.days_remaining === 0 ? 'Fundraiser ended' : undefined;

  const fmtTime = (d: Date) =>
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      <style>{STAT_TILE_CSS}</style>
      <div
        style={{
          background: `linear-gradient(135deg, ${BRAND} 0%, #1e4ba8 100%)`,
          borderRadius: 16,
          padding: '28px 32px',
          marginBottom: 28,
        }}
      >
        {/* Top row: role badge + code + refresh */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{
            background: '#ffc20e',
            color: '#7a5500',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            padding: '3px 10px',
            borderRadius: 20,
          }}>
            {role}
          </span>
          <span style={{ color: 'rgba(196,211,255,0.7)', fontSize: 13, fontFamily: 'monospace' }}>{fundraiserCode}</span>
          <span style={{ color: 'rgba(196,211,255,0.4)', fontSize: 13 }}>·</span>
          <span style={{ color: 'rgba(196,211,255,0.7)', fontSize: 13, textTransform: 'capitalize' }}>{scope} access</span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {lastUpdated && (
              <span style={{ fontSize: 11, color: 'rgba(196,211,255,0.55)', whiteSpace: 'nowrap' }}>
                Updated {fmtTime(lastUpdated)}
              </span>
            )}
            {onRefresh && (
              <button
                onClick={onRefresh}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: 6,
                  padding: '4px 12px',
                  fontSize: 12,
                  color: '#fff',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                }}
              >
                ↻ Refresh
              </button>
            )}
          </div>
        </div>

        {/* Fundraiser name */}
        <h1 style={{ color: '#fff', fontSize: 28, fontWeight: 700, margin: '0 0 16px', lineHeight: 1.2 }}>
          {fundraiserName}
        </h1>

        {/* Goal progress bar */}
        {progress !== null && stats?.goal && (
          <div style={{ maxWidth: 360, marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'rgba(196,211,255,0.8)', marginBottom: 6 }}>
              <span>Goal progress</span>
              <span>{progress}%</span>
            </div>
            <div style={{ height: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progress}%`, background: '#ffc20e', borderRadius: 4, transition: 'width 0.5s ease' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'rgba(196,211,255,0.6)', marginTop: 5 }}>
              <span>{fmt(stats.total_raised)} raised</span>
              <span>Goal: {fmt(stats.goal)}</span>
            </div>
          </div>
        )}

        {/* Stat tiles — 2×2 on mobile */}
        {stats && (
          <div className="dash-stat-tiles">
            <StatTile
              label="Total Raised"
              value={fmt(stats.total_raised)}
              sub={stats.goal ? `of ${fmt(stats.goal)} goal` : undefined}
            />
            <StatTile label="Participants" value={stats.participant_count.toLocaleString()} />
            <StatTile label="Orders" value={stats.total_orders.toLocaleString()} />
            <StatTile
              label="Days Remaining"
              value={daysValue}
              sub={daysSub}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default DashboardHeader;
